<script>
import VueApexCharts from 'vue-apexcharts'

const HEALTH_PAGE = "/captains-deck/health";
const MAX_FEATURES_PER_SLIDE = 6;

const SLIDE_LABELS = {
  summary: "Activity",
  activity: "Activity",
  turnaround: "Turnaround",
  timeSaved: "Time saved",
  plan: "Your plan",
  using: "Features",
  unused: "Features",
  annual: "Billing",
  upgrade: "Plans",
  next: "Next steps",
};

export default {
  components: {
    VueApexCharts,
  },
  data() {
    return {
      loading: true,
      loadFailed: false,
      review: null,
      index: 0,
      isFullscreen: false,
      viewportHeight: window.innerHeight,
      viewportWidth: window.innerWidth,
    };
  },
  async mounted() {
    window.addEventListener("keydown", this.onKeydown);
    window.addEventListener("resize", this.onResize);
    document.addEventListener("fullscreenchange", this.onFullscreenChange);
    try {
      this.review = await this.$A.SuperAdminService.GetBusinessReview(this.$route.params.accountId);
    } catch (e) {
      console.log(e);
      this.loadFailed = true;
    }
    this.loading = false;
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.onKeydown);
    window.removeEventListener("resize", this.onResize);
    document.removeEventListener("fullscreenchange", this.onFullscreenChange);
  },
  computed: {
    usedFeatures: function () {
      return this.review.features.filter((f) => f.used);
    },
    unusedFeatures: function () {
      return this.review.features
          .filter((f) => f.includedInPlan && !f.used)
          .slice(0, MAX_FEATURES_PER_SLIDE);
    },
    // Slides with nothing to say are left out rather than shown empty.
    slides: function () {
      let slides = ["title", "summary", "activity", "turnaround", "timeSaved", "plan"];
      if (this.usedFeatures.length) slides.push("using");
      if (this.unusedFeatures.length) slides.push("unused");
      if (this.review.annualOffer) slides.push("annual");
      if (this.review.upgrade && this.review.upgrade.unlockedFeatures.length) slides.push("upgrade");
      slides.push("next");
      return slides;
    },
    slide: function () {
      return this.slides[this.index];
    },
    slideLabel: function () {
      return SLIDE_LABELS[this.slide];
    },
    progress: function () {
      return ((this.index + 1) * 100) / this.slides.length;
    },
    // Must match the .deck font-size rule: the type scale follows the screen.
    basePx: function () {
      return Math.min(this.viewportWidth * 0.0125, this.viewportHeight * 0.0222);
    },
    periodLabel: function () {
      // periodEnd is exclusive, so the last day shown is the day before.
      let end = new Date(new Date(this.review.periodEnd).getTime() - 86400000);
      return this.formatDate(this.review.periodStart, true) + " – " + this.formatDate(end);
    },
    billingLabel: function () {
      return this.review.account.cycleMonths === 12 ? "Billed annually" : "Billed monthly";
    },
    summaryHeadline: function () {
      let sent = this.review.current.proofsSent;
      if (!sent) return "No proofs sent in the last 90 days";
      return "You sent " + sent + (sent === 1 ? " proof" : " proofs") + " in the last 90 days";
    },
    summaryStats: function () {
      let c = this.review.current;
      let p = this.review.prior;
      return [
        { label: "Proofs sent", value: c.proofsSent, trend: this.trend(c.proofsSent, p.proofsSent) },
        { label: "New versions sent", value: c.newVersionsSent, trend: this.trend(c.newVersionsSent, p.newVersionsSent) },
        { label: "Files reviewed", value: c.filesUploaded, trend: this.trend(c.filesUploaded, p.filesUploaded) },
      ];
    },
    activityHeadline: function () {
      let busiest = this.review.weeklyActivity.reduce((best, w) => (w.sent > best.sent ? w : best), { sent: 0 });
      if (!busiest.sent) return "Proofs sent and approved, by week";
      return "Your busiest week was " + this.formatDate(busiest.weekStart, true) + ", with " + busiest.sent +
          (busiest.sent === 1 ? " proof" : " proofs");
    },
    turnaroundHeadline: function () {
      let days = this.review.current.avgResponseDays;
      if (days === null) return "How quickly approvals come back";
      if (days < 1) return "Approvals come back in under a day";
      return "Approvals come back in " + days + " days on average";
    },
    turnaroundStats: function () {
      let c = this.review.current;
      let p = this.review.prior;
      return [
        {
          label: "Days to a decision",
          value: c.avgResponseDays === null ? "—" : (c.avgResponseDays < 1 ? "< 1" : c.avgResponseDays),
          note: p.avgResponseDays === null ? "" : p.avgResponseDays + " in the previous 90 days",
        },
        {
          label: "Versions per proof",
          value: c.avgVersionsPerProof,
          note: p.proofsSent ? p.avgVersionsPerProof + " in the previous 90 days" : "",
        },
        {
          label: "Approval rate",
          value: c.approvalRatePercent + "%",
          note: p.proofsSent ? p.approvalRatePercent + "% in the previous 90 days" : "",
        },
      ];
    },
    timeSavedFactors: function () {
      let t = this.review.timeSaved;
      return [
        { value: t.filesReviewed, label: t.filesReviewed === 1 ? "file reviewed" : "files reviewed" },
        { value: t.revisionsSavedPerFile, label: "revision rounds avoided per file" },
        { value: t.hoursPerRevision * 60 + " min", label: "saved per round" },
      ];
    },
    annualColumns: function () {
      let offer = this.review.annualOffer;
      return [
        {
          label: "Monthly billing",
          current: true,
          perMonth: this.money(offer.monthlyTotal),
          perYear: this.money(offer.monthlyTotal * 12),
        },
        {
          label: "Annual billing",
          current: false,
          perMonth: this.money(offer.annualTotal / 12),
          perYear: this.money(offer.annualTotal),
        },
      ];
    },
    chartHeight: function () {
      return Math.round(this.viewportHeight * 0.52);
    },
    chartSeries: function () {
      return [
        { name: "Proofs sent", data: this.review.weeklyActivity.map((w) => w.sent) },
        { name: "Approved", data: this.review.weeklyActivity.map((w) => w.approved) },
      ];
    },
    chartOptions: function () {
      let self = this;
      let labelSize = Math.round(this.basePx * 0.8) + "px";
      return {
        chart: {
          toolbar: { show: false },
          zoom: { enabled: false },
          animations: { enabled: false },
          fontFamily: "Open Sans, sans-serif",
        },
        colors: ["#019256", "#2A6FDB"],
        // 2px surface-colored gap between neighbouring bars
        stroke: { show: true, width: 2, colors: ["#ffffff"] },
        plotOptions: { bar: { columnWidth: "70%", borderRadius: 3 } },
        dataLabels: { enabled: false },
        legend: {
          position: "top",
          horizontalAlign: "left",
          fontSize: Math.round(this.basePx * 0.9) + "px",
          labels: { colors: "#092B2E" },
          markers: { radius: 2 },
          itemMargin: { horizontal: 12 },
        },
        grid: { borderColor: "#D9E2E0", strokeDashArray: 3 },
        xaxis: {
          categories: this.review.weeklyActivity.map((w) => self.formatDate(w.weekStart, true)),
          tickAmount: 13,
          labels: { rotate: 0, style: { colors: "#5B6E70", fontSize: labelSize } },
          axisBorder: { color: "#092B2E" },
          axisTicks: { show: false },
        },
        yaxis: {
          min: 0,
          forceNiceScale: true,
          labels: { formatter: (v) => Math.round(v), style: { colors: "#5B6E70", fontSize: labelSize } },
        },
        tooltip: { shared: true, intersect: false, x: { formatter: (v) => "Week of " + v } },
      };
    },
    nextSteps: function () {
      let steps = [];
      this.unusedFeatures.slice(0, 2).forEach(function (f) {
        steps.push({ title: "Try " + f.name.toLowerCase(), detail: f.description });
      });
      if (this.review.annualOffer) {
        steps.push({
          title: "Switch to annual billing",
          detail: "Save " + this.money(this.review.annualOffer.yearlySavings) + " a year on the same plan",
        });
      }
      if (this.review.upgrade && this.review.upgrade.recommended) {
        steps.push({
          title: "Move to " + this.review.upgrade.targetPlanName,
          detail: this.review.upgrade.reason,
        });
      }
      steps.push({
        title: "Tell us what's missing",
        detail: "What would make Ashore more useful for your team?",
      });
      return steps;
    },
  },
  methods: {
    onKeydown: function (e) {
      if (["ArrowRight", "PageDown", " "].indexOf(e.key) !== -1) {
        e.preventDefault();
        this.next();
      } else if (["ArrowLeft", "PageUp"].indexOf(e.key) !== -1) {
        e.preventDefault();
        this.previous();
      } else if (e.key === "Escape" && !this.isFullscreen) {
        this.exit();
      }
    },
    onResize: function () {
      this.viewportHeight = window.innerHeight;
      this.viewportWidth = window.innerWidth;
    },
    onFullscreenChange: function () {
      this.isFullscreen = document.fullscreenElement !== null;
    },
    next: function () {
      if (this.review && this.index < this.slides.length - 1) this.index++;
    },
    previous: function () {
      if (this.index > 0) this.index--;
    },
    exit: function () {
      window.location = HEALTH_PAGE;
    },
    toggleFullscreen: function () {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        this.$refs.deck.requestFullscreen();
      }
    },
    // Direction is carried by the arrow and the words, never by color alone.
    trend: function (current, prior) {
      if (!prior) return current ? { arrow: "▲", text: "Up from none in the previous 90 days" } : null;
      let percent = Math.round(((current - prior) * 100) / prior);
      if (percent === 0) return { arrow: "", text: "Level with the previous 90 days" };
      return {
        arrow: percent > 0 ? "▲" : "▼",
        text: Math.abs(percent) + "% " + (percent > 0 ? "up" : "down") + " on the previous 90 days",
      };
    },
    money: function (amount) {
      return "$" + Number(amount).toLocaleString(undefined, {
        minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
        maximumFractionDigits: 2,
      });
    },
    pad: function (number) {
      return number < 10 ? "0" + number : "" + number;
    },
    formatDate: function (date, short) {
      let options = short ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" };
      return new Date(date).toLocaleDateString(undefined, Object.assign({ timeZone: "UTC" }, options));
    },
    formatMonth: function (date) {
      return new Date(date).toLocaleDateString(undefined, { month: "long", year: "numeric", timeZone: "UTC" });
    },
  },
};
</script>
<template>
  <div ref="deck" class="deck">
    <div v-if="loading" class="deck-message">
      <b-spinner label="Loading"/>
    </div>

    <div v-else-if="loadFailed" class="deck-message">
      <p>This business review could not be loaded.</p>
      <a href="/captains-deck/health" class="deck-button">Back to Account Health</a>
    </div>

    <template v-else>
      <div class="progress-track">
        <div class="progress-fill" :style="{ width: progress + '%' }"></div>
      </div>

      <div class="stage">
        <span class="crop crop-tl"></span>
        <span class="crop crop-tr"></span>
        <span class="crop crop-bl"></span>
        <span class="crop crop-br"></span>

          <div class="slide" :class="'slide-' + slide" :key="slide">

            <template v-if="slide === 'title'">
              <div class="title-main">
                <img v-if="review.account.logoUrl" :src="review.account.logoUrl" class="title-logo" alt=""/>
                <div class="eyebrow">Business review</div>
                <h1 class="title-name">{{ review.account.name }}</h1>
              </div>
              <dl class="title-panel">
                <div>
                  <dt>Review period</dt>
                  <dd>{{ periodLabel }}</dd>
                </div>
                <div>
                  <dt>Plan</dt>
                  <dd>{{ review.account.planName }}</dd>
                </div>
                <div>
                  <dt>Customer since</dt>
                  <dd>{{ formatMonth(review.account.customerSince) }}</dd>
                </div>
              </dl>
            </template>

            <template v-else>
              <div class="eyebrow">{{ slideLabel }}</div>

              <template v-if="slide === 'summary'">
                <h2 class="headline">{{ summaryHeadline }}</h2>
                <div class="stats">
                  <div v-for="stat in summaryStats" :key="stat.label" class="stat">
                    <div class="stat-value">{{ stat.value }}</div>
                    <div class="stat-label">{{ stat.label }}</div>
                    <div class="stat-note" v-if="stat.trend">
                      <span class="stat-arrow" v-if="stat.trend.arrow">{{ stat.trend.arrow }}</span>{{ stat.trend.text }}
                    </div>
                  </div>
                </div>
                <div class="footnote">
                  Since {{ formatMonth(review.account.customerSince) }}:
                  <strong>{{ review.lifetime.proofsSent }}</strong> proofs sent,
                  <strong>{{ review.lifetime.proofsApproved }}</strong> approved,
                  <strong>{{ review.lifetime.filesUploaded }}</strong> files reviewed.
                </div>
              </template>

              <template v-else-if="slide === 'activity'">
                <h2 class="headline">{{ activityHeadline }}</h2>
                <div class="chart">
                  <VueApexCharts type="bar" :height="chartHeight" :options="chartOptions" :series="chartSeries"/>
                </div>
              </template>

              <template v-else-if="slide === 'turnaround'">
                <h2 class="headline">{{ turnaroundHeadline }}</h2>
                <div class="stats">
                  <div v-for="stat in turnaroundStats" :key="stat.label" class="stat">
                    <div class="stat-value">{{ stat.value }}</div>
                    <div class="stat-label">{{ stat.label }}</div>
                    <div class="stat-note">{{ stat.note }}</div>
                  </div>
                </div>
              </template>

              <template v-else-if="slide === 'timeSaved'">
                <h2 class="headline">Time back for your team</h2>
                <div class="hero">
                  <div class="hero-value">{{ review.timeSaved.hoursSaved }}<span class="hero-unit">hours</span></div>
                  <div class="hero-label">
                    saved in the last 90 days. At this pace, about
                    <strong>{{ review.timeSaved.hoursSavedPerYear }} hours a year</strong>.
                  </div>
                </div>
                <div class="factors">
                  <span class="factors-label">How we estimate it</span>
                  <template v-for="(factor, i) in timeSavedFactors">
                    <span class="factors-times" v-if="i > 0" :key="'x' + i">×</span>
                    <span class="factor" :key="factor.label"><strong>{{ factor.value }}</strong> {{ factor.label }}</span>
                  </template>
                </div>
              </template>

              <template v-else-if="slide === 'plan'">
                <h2 class="headline">Your {{ review.account.planName }} plan</h2>
                <div class="stats">
                  <div class="stat stat-wide">
                    <div class="stat-value">
                      {{ review.planUsage.filesSentThisCycle }}
                      <span class="stat-of" v-if="!review.planUsage.unlimitedFiles">of {{ review.planUsage.fileAllowance }}</span>
                    </div>
                    <div class="stat-label">Files sent this billing period</div>
                    <div class="stat-note" v-if="review.planUsage.unlimitedFiles">Unlimited files on your plan</div>
                    <template v-else>
                      <div class="meter">
                        <div class="meter-fill" :style="{ width: Math.min(review.planUsage.filesUsedPercent, 100) + '%' }"></div>
                      </div>
                      <div class="stat-note">{{ review.planUsage.filesUsedPercent }}% of your allowance used</div>
                    </template>
                  </div>
                  <div class="stat">
                    <div class="stat-value">{{ review.planUsage.userCount }}</div>
                    <div class="stat-label">{{ review.planUsage.userCount === 1 ? "User" : "Users" }} on your account</div>
                    <div class="stat-note">{{ billingLabel }}</div>
                  </div>
                </div>
              </template>

              <template v-else-if="slide === 'using'">
                <h2 class="headline">
                  {{ usedFeatures.length }} {{ usedFeatures.length === 1 ? "feature" : "features" }} working for you
                </h2>
                <ul class="features">
                  <li v-for="feature in usedFeatures" :key="feature.key" class="feature">
                    <span class="feature-mark feature-mark-on">✓</span>
                    <div>
                      <div class="feature-name">{{ feature.name }}</div>
                      <div class="feature-description">{{ feature.description }}</div>
                    </div>
                  </li>
                </ul>
              </template>

              <template v-else-if="slide === 'unused'">
                <h2 class="headline">More you can turn on today</h2>
                <p class="lead">Already included in your {{ review.account.planName }} plan.</p>
                <ul class="features">
                  <li v-for="feature in unusedFeatures" :key="feature.key" class="feature">
                    <span class="feature-mark">+</span>
                    <div>
                      <div class="feature-name">{{ feature.name }}</div>
                      <div class="feature-description">{{ feature.description }}</div>
                    </div>
                  </li>
                </ul>
              </template>

              <template v-else-if="slide === 'annual'">
                <h2 class="headline">
                  Save {{ money(review.annualOffer.yearlySavings) }} a year with annual billing
                </h2>
                <p class="lead">Same plan, same team, {{ review.annualOffer.savingsPercent }}% less.</p>
                <div class="prices">
                  <div v-for="column in annualColumns" :key="column.label" class="price"
                       :class="{ 'price-offer': !column.current }">
                    <div class="price-label">
                      {{ column.label }}<span class="price-tag" v-if="column.current">Today</span>
                    </div>
                    <div class="price-value">{{ column.perMonth }}<span class="price-unit">/ month</span></div>
                    <div class="price-note">{{ column.perYear }} a year</div>
                  </div>
                </div>
              </template>

              <template v-else-if="slide === 'upgrade'">
                <h2 class="headline">What {{ review.upgrade.targetPlanName }} adds</h2>
                <p class="lead" v-if="review.upgrade.recommended">{{ review.upgrade.reason }}.</p>
                <ul class="features">
                  <li v-for="feature in review.upgrade.unlockedFeatures" :key="feature.key" class="feature">
                    <span class="feature-mark">+</span>
                    <div>
                      <div class="feature-name">{{ feature.name }}</div>
                      <div class="feature-description">{{ feature.description }}</div>
                    </div>
                  </li>
                </ul>
                <div class="footnote">
                  {{ review.upgrade.targetPlanName }} for your team:
                  <strong>{{ money(review.upgrade.targetMonthlyTotal) }} a month</strong>.
                </div>
              </template>

              <template v-else-if="slide === 'next'">
                <h2 class="headline">Where to go from here</h2>
                <ul class="steps">
                  <li v-for="step in nextSteps" :key="step.title" class="step">
                    <span class="step-box"></span>
                    <div>
                      <div class="step-title">{{ step.title }}</div>
                      <div class="step-detail">{{ step.detail }}</div>
                    </div>
                  </li>
                </ul>
              </template>
            </template>
          </div>

        <div class="slug">
          <span class="slug-text">Business review · {{ review.account.name }}</span>
          <span class="slug-controls">
            <button class="deck-button" :disabled="index === 0" @click="previous">Previous</button>
            <span class="slug-count">{{ pad(index + 1) }} / {{ pad(slides.length) }}</span>
            <button class="deck-button" :disabled="index === slides.length - 1" @click="next">Next</button>
          </span>
          <span class="slug-actions">
            <button class="deck-button" @click="toggleFullscreen">{{ isFullscreen ? "Exit full screen" : "Full screen" }}</button>
            <button class="deck-button" @click="exit">Exit</button>
          </span>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>

/* Everything is sized in em from this one rule, so the deck scales with the screen. */
.deck {
  --ink: #092B2E;
  --green: #019256;
  --mist: #F2F6F5;
  --rule: #D9E2E0;
  --muted: #5B6E70;

  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  background: white;
  color: var(--ink);
  font-family: 'Open Sans', sans-serif;
  font-size: min(1.25vw, 2.22vh);
  line-height: 1.4;
}

.deck-message {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.progress-track {
  height: 3px;
  background: var(--rule);
}

.progress-fill {
  height: 100%;
  background: var(--green);
}

.stage {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 3.4em 6em 1.6em;
}

/* Print crop marks: two short lines per corner that stop short of meeting. */
.crop {
  position: absolute;
  width: 1.5em;
  height: 1.5em;
  pointer-events: none;
}

.crop::before,
.crop::after {
  content: "";
  position: absolute;
  background: var(--ink);
  opacity: 0.45;
}

.crop::before {
  width: 1px;
  height: 1em;
}

.crop::after {
  width: 1em;
  height: 1px;
}

.crop-tl { top: 1.2em; left: 1.6em; }
.crop-tr { top: 1.2em; right: 1.6em; }
.crop-bl { bottom: 1.2em; left: 1.6em; }
.crop-br { bottom: 1.2em; right: 1.6em; }

.crop-tl::before { left: 1.5em; top: 0; }
.crop-tl::after { top: 1.5em; left: 0; }
.crop-tr::before { right: 1.5em; top: 0; }
.crop-tr::after { top: 1.5em; right: 0; }
.crop-bl::before { left: 1.5em; bottom: 0; }
.crop-bl::after { bottom: 1.5em; left: 0; }
.crop-br::before { right: 1.5em; bottom: 0; }
.crop-br::after { bottom: 1.5em; right: 0; }

.slide {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.eyebrow {
  font-family: 'Montserrat', sans-serif;
  font-size: 0.85em;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--green);
}

.headline {
  max-width: 25em;
  margin: 0.35em 0 0;
  font-family: 'Montserrat', sans-serif;
  font-size: 2.7em;
  font-weight: 700;
  line-height: 1.12;
  letter-spacing: -0.015em;
  color: var(--ink);
}

.lead {
  margin: 0.7em 0 0;
  font-size: 1.35em;
  color: var(--muted);
}

.footnote {
  margin-top: auto;
  padding-top: 1.2em;
  border-top: 1px solid var(--rule);
  font-size: 1.05em;
  color: var(--muted);
}

.footnote strong {
  color: var(--ink);
  font-weight: 600;
}

/* Title */
.slide-title {
  flex-direction: row;
  align-items: stretch;
  gap: 4em;
}

.title-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
}

.title-logo {
  align-self: flex-start;
  max-height: 5.5em;
  max-width: 18em;
  margin-bottom: 3em;
}

.title-name {
  margin: 0.15em 0 0;
  font-family: 'Montserrat', sans-serif;
  font-size: 6em;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.03em;
  color: var(--ink);
  overflow-wrap: anywhere;
}

.title-panel {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2em;
  width: 21em;
  margin: 1.2em 0;
  padding: 3em;
  border-radius: 0.4em;
  background: var(--ink);
  color: white;
}

.title-panel dt {
  font-family: 'Montserrat', sans-serif;
  font-size: 0.8em;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #7FD9B4;
}

.title-panel dd {
  margin: 0.2em 0 0;
  font-size: 1.6em;
  font-weight: 600;
}

/* Stats */
.stats {
  display: flex;
  margin: auto 0;
}

.stat {
  flex: 1;
  padding: 0 2.5em;
  border-left: 1px solid var(--rule);
}

.stat:first-child {
  padding-left: 0;
  border-left: none;
}

.stat-wide {
  flex: 1.6;
}

.stat-value {
  font-family: 'Montserrat', sans-serif;
  font-size: 6.5em;
  font-weight: 600;
  line-height: 1;
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
}

.stat-of {
  font-size: 0.32em;
  font-weight: 500;
  letter-spacing: 0;
  color: var(--muted);
}

.stat-label {
  margin-top: 0.7em;
  font-size: 1.3em;
  font-weight: 600;
}

.stat-note {
  margin-top: 0.3em;
  font-size: 1em;
  color: var(--muted);
}

.stat-arrow {
  margin-right: 0.45em;
  font-size: 0.75em;
  color: var(--ink);
}

.meter {
  height: 0.6em;
  margin-top: 1.2em;
  border-radius: 0.3em;
  background: var(--rule);
  overflow: hidden;
}

.meter-fill {
  height: 100%;
  min-width: 0.6em;
  border-radius: 0.3em;
  background: var(--green);
}

/* Chart */
.chart {
  flex: 1;
  min-height: 0;
  margin-top: 1.2em;
}

/* Hero */
.hero {
  margin: auto 0;
}

.hero-value {
  font-family: 'Montserrat', sans-serif;
  font-size: 11em;
  font-weight: 700;
  line-height: 0.95;
  letter-spacing: -0.04em;
  color: var(--green);
  font-variant-numeric: tabular-nums;
}

.hero-unit {
  margin-left: 0.25em;
  font-size: 0.3em;
  font-weight: 600;
  letter-spacing: 0;
  color: var(--ink);
}

.hero-label {
  max-width: 30em;
  margin-top: 0.8em;
  font-size: 1.5em;
}

.factors {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.8em;
  padding-top: 1.2em;
  border-top: 1px solid var(--rule);
  font-size: 1.05em;
  color: var(--muted);
}

.factors-label {
  margin-right: 0.6em;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.78em;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.factor {
  padding: 0.35em 0.9em;
  border-radius: 2em;
  background: var(--mist);
}

.factor strong {
  color: var(--ink);
  font-weight: 700;
}

/* Features */
.features {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.6em 4em;
  margin: auto 0;
  padding: 0;
  list-style: none;
}

.feature {
  display: flex;
  gap: 1em;
}

.feature-mark {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.9em;
  height: 1.9em;
  border: 2px solid var(--green);
  border-radius: 50%;
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
  line-height: 1;
  color: var(--green);
}

.feature-mark-on {
  background: var(--green);
  color: white;
}

.feature-name {
  font-family: 'Montserrat', sans-serif;
  font-size: 1.4em;
  font-weight: 600;
  line-height: 1.3;
}

.feature-description {
  margin-top: 0.15em;
  font-size: 1.1em;
  color: var(--muted);
}

/* Annual */
.prices {
  display: flex;
  gap: 2em;
  margin: auto 0;
}

.price {
  flex: 1;
  padding: 2.2em 2.5em;
  border: 1px solid var(--rule);
  border-radius: 0.4em;
}

.price-offer {
  border-color: var(--ink);
  background: var(--ink);
  color: white;
}

.price-label {
  font-family: 'Montserrat', sans-serif;
  font-size: 0.9em;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.price-tag {
  margin-left: 0.9em;
  padding: 0.2em 0.7em;
  border-radius: 2em;
  background: var(--mist);
  letter-spacing: 0.08em;
  color: var(--muted);
}

.price-value {
  margin-top: 0.25em;
  font-family: 'Montserrat', sans-serif;
  font-size: 5em;
  font-weight: 600;
  line-height: 1.1;
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
}

.price-unit {
  margin-left: 0.4em;
  font-size: 0.26em;
  font-weight: 500;
  letter-spacing: 0;
}

.price-note {
  margin-top: 0.4em;
  font-size: 1.2em;
  opacity: 0.75;
}

/* Next steps */
.steps {
  display: flex;
  flex-direction: column;
  gap: 1.5em;
  margin: auto 0;
  padding: 0;
  list-style: none;
}

.step {
  display: flex;
  gap: 1.2em;
}

.step-box {
  flex: none;
  width: 1.5em;
  height: 1.5em;
  margin-top: 0.35em;
  border: 2px solid var(--ink);
  border-radius: 0.2em;
}

.step-title {
  font-family: 'Montserrat', sans-serif;
  font-size: 1.7em;
  font-weight: 600;
  line-height: 1.25;
}

.step-detail {
  font-size: 1.2em;
  color: var(--muted);
}

/* Slug line */
.slug {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1.4em;
  padding: 0 1.6em;
  font-family: 'Montserrat', sans-serif;
  font-size: max(11px, 0.68em);
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--muted);
}

.slug-text,
.slug-actions {
  flex: 1;
}

.slug-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1.8em;
}

.slug-controls {
  display: flex;
  align-items: center;
  gap: 1.8em;
}

.slug-count {
  font-variant-numeric: tabular-nums;
  color: var(--ink);
}

.deck-button {
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  color: var(--green);
  cursor: pointer;
}

.deck-button:disabled {
  color: var(--rule);
  cursor: default;
}

.deck-button:focus-visible {
  outline: 2px solid var(--green);
  outline-offset: 4px;
}

</style>
